import { memo } from 'react'
import Link from 'next/link'
import { i18n } from 'next-i18next'
import styled, { css } from 'styled-components'
import { Box, Link as DSLink, Skeleton } from '@machobear/design-system'

import { colors } from '@/styles'
import { getUrl, isGroup } from '@/utils/listings'
import defaultBuildingImage from '@/features/Listings/map/default-building.jpg'
import CdnImage from '@/components/cdn-image'
import { ListingCardActions } from '@/components/listing-card/actions'
import PriceDisplay from '@/components/listing-card/pricing-display'
import { PillWrapper, VerifiedPill } from '@/components/pill'
import { Label } from '@/components/text'

import * as styles from './popup.css'

const LowerTextWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${colors.grey32};
  width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;

  div {
    :first-child {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }
    :last-child {
      flex-shrink: 0;
    }
  }
`

const priceStyles = css`
  font-size: 16px;
  line-height: 24px;
`

const frequencyStyles = css`
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
`

const StyledPillWrapper = styled(PillWrapper)`
  top: 6px;
  left: 6px;
`

const renderBedBath = ({ bedrooms, bathrooms, unit_type_scope_txt_id }) => {
  if (unit_type_scope_txt_id === 'SINGLE') {
    return i18n.t('common.room', 'Room')
  }
  return `${
    bedrooms > 0
      ? `${bedrooms} ${i18n.t('common.bed', 'Bed')}`
      : i18n.t('common.studio', 'Studio')
  } · ${bathrooms} ${i18n.t('common.bath', 'Bath')}`
}

const SkeletonState = () => {
  return (
    <>
      <Box position="relative" width="full" style={{ height: '150px' }}>
        <Skeleton.Shape width="100%" height="100%" />
      </Box>
      <Box display="flex" flexDirection="column" gap="xsmall" padding="xsmall">
        <Skeleton.Shape width="105px" height="24px" />
        <LowerTextWrapper>
          <Skeleton.Shape width="80px" height="12px" /> ·
          <Skeleton.Shape width="30px" height="12px" /> ·
          <Skeleton.Shape width="30px" height="12px" />
        </LowerTextWrapper>
      </Box>
    </>
  )
}

const SingleUnit = ({ data }) => {
  if (!data) return <SkeletonState />
  const listingCover = data.cover_photo_aws_s3_key
  const cover = listingCover || defaultBuildingImage
  const price = isGroup(data)
    ? { min: data.gr_min_price, max: data.gr_max_price }
    : data.price
  const isVerified = data.verified_state_machine === 'approved'

  return (
    <Link href={getUrl(data)} passHref legacyBehavior>
      <DSLink
        rel="noopener noreferrer"
        target="_blank"
        className={styles.popup}
      >
        <Box position="relative" width="full" style={{ height: '150px' }}>
          <CdnImage
            src={cover}
            sizes="240px"
            placeholder="shimmer"
            layout="fill"
            objectFit="cover"
            alt=""
          />
          <StyledPillWrapper>
            {isVerified && <VerifiedPill disableTooltip />}
          </StyledPillWrapper>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          gap="xxxsmall"
          padding="xxsmall"
        >
          <PriceDisplay
            price={price}
            frequency={data.price_frequency}
            priceStyles={priceStyles}
            frequencyStyles={frequencyStyles}
          />
          <LowerTextWrapper>
            <Label fontSize="12px">{data.street_name}</Label>
            <Label fontSize="12px"> · </Label>
            <Label fontSize="12px">{renderBedBath(data)}</Label>
          </LowerTextWrapper>

          <Box marginTop="small">
            <ListingCardActions
              listing={data}
              messageClickId="message-map-click"
              stackButtons
            />
          </Box>
        </Box>
      </DSLink>
    </Link>
  )
}

export default memo(SingleUnit)
